import { useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { defer, sample } from 'lodash';

import { ExplorationChat } from '@/generated/operation-documents';
import { ChatExploration, ChatMessage, Exploration, QueryVariables } from '@/explore/types';
import { exportChatMessage, exportExploration } from '@/explore/output';
import { generateUUID } from '@/lib/utils';
import { convertChatMessageType } from '@/explore/input';
import { isTimeoutError } from '@/lib/error/utils/timeout';

const ProgressStepRequestTimeout = 8 * 60 * 1000;
const TotalRequestTimeout = 10 * 60 * 1000;
const LoadingMessages = [
  'Analyzing your question',
  'Searching through data',
  'Thinking of a great solution',
  'Retrieving relevant data',
  'Analyzing the information',
  'Ruminating on the question',
  'Conjuring up a response',
  'Tapping into the collective unconscious',
  'Channeling the wisdom of the ages',
  'Plotting the trajectory of insight',
  'Thinking really hard',
  'Accessing mainframe...',
  'Reticulating splines...',
] as const;

type Params = {
  conversationId: string;
  progressStepTimeout?: number;
  totalTimeout?: number;
  onTimeout?: () => void;
  onAbort?: () => void;
  onMessage: (message: ChatMessage | ChatExploration) => void;
  onProgress?: (message: string) => void;
  onComplete: () => void;
  onError?: (error: Error) => void;
};

export const useChatStream = ({
  conversationId,
  progressStepTimeout = ProgressStepRequestTimeout,
  totalTimeout = TotalRequestTimeout,
  onTimeout,
  onMessage,
  onProgress,
  onComplete,
  onError,
  onAbort,
}: Params) => {
  const apolloClient = useApolloClient();

  const userAbortRef = useRef(new AbortController());
  const timeoutAbortRef = useRef(new AbortController());
  const progressTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
  const requestTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  const [status, setStatus] = useState<'idle' | 'loading' | 'streaming' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState<string>();

  const resetProgressTimeout = () => {
    if (progressTimeoutRef.current !== null) {
      clearTimeout(progressTimeoutRef.current);
    }
    progressTimeoutRef.current = setTimeout(
      () => timeoutAbortRef.current.abort('Progress step timeout'),
      progressStepTimeout,
    );
  };

  const setRequestTimeouts = () => {
    resetProgressTimeout();
    if (requestTimeoutRef.current !== null) {
      clearTimeout(requestTimeoutRef.current);
    }
    timeoutAbortRef.current = new AbortController();
    timeoutAbortRef.current.signal.addEventListener('abort', handleTimeout, {
      once: true,
    });
    requestTimeoutRef.current = setTimeout(
      () => timeoutAbortRef.current.abort('Total timeout'),
      totalTimeout,
    );
  };

  const clearTimeouts = () => {
    if (progressTimeoutRef.current !== null) {
      clearTimeout(progressTimeoutRef.current);
      progressTimeoutRef.current = null;
    }
    if (requestTimeoutRef.current !== null) {
      clearTimeout(requestTimeoutRef.current);
      requestTimeoutRef.current = null;
    }
  };

  const clearStatus = () => {
    setStatus('idle');
    setStatusMessage(undefined);
  };

  const handleTimeout = () => {
    clearTimeouts();
    clearStatus();
    onTimeout?.();
  };

  const handleAbort = () => {
    userAbortRef.current.abort('User aborted the request');
    clearTimeouts();
    clearStatus();
    onAbort?.();
  };

  const handleMessage = (message: ChatMessage | ChatExploration) => {
    setStatus('streaming');
    onMessage(message);
  };

  const handleProgress = (message: string) => {
    resetProgressTimeout();
    setStatusMessage(message);
    onProgress?.(message);
  };

  const handleData = (response: any) => {
    if (response.data.ask.lastMessage === undefined) {
      return;
    }
    const message = convertChatMessageType(response.data.ask.lastMessage);
    message.type === 'progress' ? handleProgress(message.message) : handleMessage(message);
  };

  const handleComplete = () => {
    clearTimeouts();
    clearStatus();
    onComplete();
  };

  const handleError = (error: Error) => {
    clearTimeouts();
    if (isTimeoutError(error)) {
      handleTimeout();
      return;
    }
    setStatus('error');
    onError?.(error);
  };

  const handleSend = (params: {
    accountId: string;
    exploration: Exploration;
    variables: QueryVariables;
    selectors: string[];
    messages: (ChatMessage | ChatExploration)[];
  }) => {
    const { accountId, exploration, variables, selectors, messages } = params;

    setStatusMessage(sample(LoadingMessages));
    setStatus('loading');
    setRequestTimeouts();

    userAbortRef.current = new AbortController();

    const observer = apolloClient.subscribe({
      query: ExplorationChat,
      variables: {
        accountId,
        exploration: exportExploration(exploration),
        variables,
        correlationId: generateUUID(),
        selectors,
        conversation: {
          conversationId,
          messages: messages.map(exportChatMessage),
        },
      },
      context: {
        fetchOptions: {
          signal: AbortSignal.any([userAbortRef.current.signal, timeoutAbortRef.current.signal]),
        },
      },
    });

    observer.subscribe(
      (data) => defer(handleData.bind(null, data as any)),
      handleError,
      () => defer(handleComplete),
    );
  };

  return {
    status,
    statusMessage,
    abort: handleAbort,
    send: handleSend,
  };
};
